import {
  CSVRow,
  displayDateTimeToMinute,
  Entity,
  FieldType,
  makeClone,
  Primitive,
  validate,
  validateEmailAddress,
} from 'gossamer-unified';
import { Event } from './Event';

export interface EventUploadRow {
  title: string;
  type: string;
  event_date: string; // in spreadsheet
  start_time: string; // in spreadsheet
  end_time: string; // in spreadsheet
  start_date: string; // in API payload
  end_date: string; // in API payload
  event_profile_id: number;
  main_host_email_address: string;
  co_host_email_addresses: string;
  location_type: string;
  location: string;
  markdown_description: string;
  specialty_group: string;
  employment_grade: string;
  year_group: string;
  groups_to_allocate_to: string;
  book_to_users_email_addresses: string;
  max_number_of_tickets: number;
  gps_location: number;
}

const clone = makeClone();

export const EventUpload: Entity<EventUploadRow> = {
  id: 'EventUpload',
  title: 'Event',
  service: 'main',
  rowLabel: (row: EventUploadRow) => `${row.title} (${displayDateTimeToMinute(row.start_date)})`,
  primaryKeyFields: [],
  security: [],
  fields: {
    title: clone(Event.fields.title, { seq: 5, listColumn: true }),
    type: clone(Event.fields.type, { seq: 10, listColumn: true }),
    event_date: {
      seq: 15,
      type: FieldType.Date,
      label: 'Event Date',
      mandatory: true,
      listColumn: true,
      description: "in the format 'dd/mm/yyyy'",
    },
    start_time: {
      seq: 20,
      type: FieldType.Text,
      label: 'Start Time',
      mandatory: true,
      listColumn: true,
      description: "in the format 'hh:mm'",
    },
    end_time: {
      seq: 25,
      type: FieldType.Text,
      label: 'End Time',
      mandatory: true,
      listColumn: true,
      description: "in the format 'hh:mm'",
    },
    start_date: {
      seq: 30,
      type: FieldType.Datetime,
      label: 'Start Date/time',
      visible: false,
    },
    end_date: {
      seq: 35,
      type: FieldType.Datetime,
      label: 'End Date/time',
      visible: false,
    },
    event_profile_id: clone(Event.fields.event_profile_id, {
      seq: 40,
      mandatory: true,
      description: 'id number of the Event Profile to use for this Event',
    }),
    main_host_email_address: {
      seq: 45,
      type: FieldType.Text,
      label: 'Main Host Email Address',
      mandatory: false,
      description: 'user email address - if not supplied, defaults to the user performing the upload',
    },
    co_host_email_addresses: {
      seq: 47,
      type: FieldType.Text,
      label: 'Co-Host Email Addresses',
      mandatory: false,
      description: 'must be a list of email addresses separated by the "|" character',
    },
    markdown_description: {
      seq: 50,
      type: FieldType.Text,
      label: 'Description',
      description: 'description text for this event',
    },
    location_type: clone(Event.fields.location_type, { seq: 55, listColumn: true }),
    location: clone(Event.fields.location, {
      seq: 60,
      listColumn: true,
      description: "required if Location Type is 'in-person' or 'hybrid', otherwise must be blank",
    }),
    specialty_group: clone(Event.fields.specialty_group, { seq: 65 }),
    employment_grade: clone(Event.fields.employment_grade, { seq: 70 }),
    year_group: clone(Event.fields.year_group, { seq: 75 }),
    groups_to_allocate_to: {
      seq: 80,
      type: FieldType.Text,
      label: 'Id(s) of Group(s) to Add this Event to',
      description: 'must be a list of numbers separated by the "|" character',
      regex: '^[\\d|]+$',
    },
    book_to_users_email_addresses: {
      seq: 85,
      type: FieldType.Text,
      label: 'Email Addresses of Users to Book this Event to',
      description: 'must be a list of email addresses separated by the "|" character',
    },
    max_number_of_tickets: {
      seq: 90,
      type: FieldType.Integer,
      label: 'Number of Tickets',
      description: 'the maximum number of users that can book on to this event - defaults to 10',
      min: 1,
    },
    gps_location: {
      seq: 95,
      type: FieldType.Integer,
      label: 'GPS Location Id',
      description: 'the ID of the GPS location associated with this event',
    },
  },
};

export const eventValidator = (row: CSVRow<EventUploadRow>): string[] => {
  const out: string[] = [];
  (Object.keys(EventUpload.fields) as (keyof EventUploadRow)[]).forEach((fieldId) => {
    const field = EventUpload.fields[fieldId];
    const value = row[fieldId];
    applyValidation(out, validate(field, value), field.label, value);
  });

  // sort out date/times
  const date_match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(row.event_date || '');
  const start_match = /^(\d{2}):(\d{2})$/.exec(row.start_time || '');
  const end_match = /^(\d{2}):(\d{2})$/.exec(row.end_time || '');
  if (!!row.event_date && (!date_match || date_match.length < 4)) {
    out.push(`Invalid Event Date: '${row.event_date}' should be 'dd/mm/yyyy'`);
  }
  if (!!row.start_time && (!start_match || start_match.length < 3)) {
    out.push(`Invalid Start Time: '${row.start_time}' should be 'hh:mm'`);
  }
  if (!!row.end_time && (!end_match || end_match.length < 3)) {
    out.push(`Invalid End Time: '${row.end_time}' should be 'hh:mm'`);
  }

  if (date_match?.length === 4 && start_match?.length === 3 && end_match?.length === 3) {
    const start_date = toUTCDateTime(date_match, start_match);
    const end_date = toUTCDateTime(date_match, end_match);
    if (!start_date) {
      out.push(`Invalid Event Date/Time: '${row.event_date} ${row.start_time}' is not a real date and time`);
    } else {
      row.start_date = start_date;
    }
    if (!end_date) {
      out.push(`Invalid Event Date/Time: '${row.event_date} ${row.end_time}' is not a real date and time`);
    } else {
      row.end_date = end_date;
    }
  }

  if (row.type === 'on-demand' && row.location_type !== 'online') {
    out.push(`If Type is 'on-demand' then Location Type must be 'online'`);
  }
  if (row.type !== 'on-demand' && !row.start_date) {
    out.push(`Start Date/time: required since Type is not 'on-demand'`);
  }
  if (row.type === 'on-demand' && !!row.start_date) {
    out.push(`Start Date/time: must be blank since Type is 'on-demand'`);
  }
  if (row.type !== 'on-demand' && !row.end_date) {
    out.push(`End Date/time: required since Type is not 'on-demand'`);
  }
  if (row.type === 'on-demand' && !!row.end_date) {
    out.push(`End Date/time: must be blank since Type is 'on-demand'`);
  }
  applyValidation(
    out,
    validateEmailAddress(row.main_host_email_address),
    'Main Host Email Address',
    row.main_host_email_address,
  );
  applyValidation(
    out,
    validateEmailAddressList(row.co_host_email_addresses),
    'Co-Host Email Addresses',
    row.co_host_email_addresses,
  );
  if (['in-person', 'hybrid'].indexOf(row.location_type || '') > -1 && !row.location) {
    out.push(`Location: required since Location Type is '${row.location_type}'`);
  }
  if (['in-person', 'hybrid'].indexOf(row.location_type || '') === -1 && !!row.location) {
    out.push(`Location: must be blank since Location Type is '${row.location_type}'`);
  }
  applyValidation(
    out,
    validateGroupIdList(row.groups_to_allocate_to),
    'Groups to Allocate To',
    row.groups_to_allocate_to,
  );
  applyValidation(
    out,
    validateEmailAddressList(row.book_to_users_email_addresses),
    'Email Addresses',
    row.book_to_users_email_addresses,
  );
  if (!!row.gps_location && row.location_type === 'online') {
    out.push(`GPS Location Id: must be blank since Location Type is 'online'`);
  }
  return out;
};

export const applyValidation = (out: string[], validation: string | null, fieldLabel: string, value: Primitive) => {
  if (validation) {
    out.push(`${fieldLabel}: '${value || ''}' ${validation}`);
  }
};

export const validateGroupIdList = (groupIdList: string | null | undefined): string | null => {
  if (!groupIdList) {
    return null;
  }
  const groupIds = groupIdList.split('|');
  for (let i = 0; i < groupIds.length; i += 1) {
    if (!/^\d+$/.test(groupIds[i])) {
      return `Group Ids must be a list of numbers separated by the "|" character`;
    }
  }
  return null;
};

export const validateEmailAddressList = (emailAddressList: string | null | undefined): string | null => {
  if (!emailAddressList) {
    return null;
  }
  const emailAddresses = emailAddressList.split('|');
  for (let i = 0; i < emailAddresses.length; i += 1) {
    const validation = validateEmailAddress(emailAddresses[i]);
    if (validation) {
      return `${emailAddresses[i]} is not a valid email address`;
    }
  }
  return null;
};

// use new Date(str).toISOString() to convert local TZ date/time string to UTC
const toUTCDateTime = (date_match: RegExpExecArray, time_match: RegExpExecArray): string | null => {
  const date = new Date(
    `${pad(date_match[3], '2000')}-${pad(date_match[2], '00')}-${pad(date_match[1], '00')}T${pad(
      time_match[1],
      '00',
    )}:${pad(time_match[2], '00')}:00`,
  );
  return isNaN(date.getTime()) ? null : date.toISOString();
};

const pad = (main: string, padWith: string) => padWith.substring(0, padWith.length - main.length) + main;
